// src/pages/Catalog.js
import { useState, useRef, useEffect, useCallback } from 'react';
import { faFire, faClock, faStar, faBoxes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import catalogData from '../../data/CatalogList';
import './Catalog.css';

export default function Catalog() {
  // modalContent: { title, items: [{type:'image'|'video', src}], index }
  const [modalContent, setModalContent] = useState(null);

  const openModal = (items, index, title) => {
    setModalContent({ items, index, title });
    document.body.style.overflow = 'hidden'; // prevent background scroll
  };

  const closeModal = () => {
    setModalContent(null);
    document.body.style.overflow = '';
  };

  const next = useCallback(() => {
    setModalContent(c => (!c ? c : { ...c, index: (c.index + 1) % c.items.length }));
  }, []);

  const prev = useCallback(() => {
    setModalContent(c => (!c ? c : { ...c, index: (c.index - 1 + c.items.length) % c.items.length }));
  }, []);

  // Keyboard navigation for modal
  useEffect(() => {
    if (!modalContent) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeModal();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [modalContent, next, prev]);

  const sectionIcons = {
    bestSellers: faFire,
    comingSoon: faClock,
    newArrivals: faStar,
    fullCollection: faBoxes,
  };

  return (
    <div className="catalog-container">
      <h1 className="catalog-heading">Our Photobook Collection</h1>
      <p className="catalog-subtitle">Hover to browse — Click image or video to view fullscreen</p>
      <div className="catalog-intro">
        <p>
          We offer a wide variety of styles, vibrant colors, and premium paper finishes —
          because your memories deserve more than just pages. <strong>Your story is our passion.</strong>
        </p>
      </div>

      {Object.entries(catalogData).map(([section, items]) => (
        <div key={section} className="catalog-section">
          <h2 className="section-heading">
            <FontAwesomeIcon icon={sectionIcons[section]} />{' '}
            {section === 'comingSoon'
              ? 'Coming Soon'
              : section === 'newArrivals'
              ? 'New Arrivals'
              : section === 'bestSellers'
              ? 'Top 6 Sellers'
              : 'Complete Collection'}
          </h2>

          <div className="catalog-grid">
            {items.map((book, index) => (
              <CatalogTile key={index} book={book} openModal={openModal} />
            ))}
          </div>
        </div>
      ))}

      {modalContent && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content modal-gallery" onClick={(e) => e.stopPropagation()}>
            <span className="close-btn" onClick={closeModal}>×</span>

            <h3 className="modal-title">{modalContent.title}</h3>

            <button className="modal-nav prev" onClick={prev} aria-label="Previous">❮</button>

            <div className="modal-viewer">
              {modalContent.items[modalContent.index].type === 'image' ? (
                <img
                  src={modalContent.items[modalContent.index].src}
                  alt={`${modalContent.title}-${modalContent.index + 1}`}
                />
              ) : (
                <video
                  src={modalContent.items[modalContent.index].src}
                  controls
                  autoPlay
                  controlsList="nodownload"
                  onContextMenu={(e) => e.preventDefault()}
                />
              )}
            </div>

            <button className="modal-nav next" onClick={next} aria-label="Next">❯</button>

            <div className="modal-thumbs">
              {modalContent.items.map((it, i) => (
                <button
                  key={i}
                  className={`thumb ${i === modalContent.index ? 'active' : ''}`}
                  onClick={() => setModalContent(c => ({ ...c, index: i }))}
                  aria-label={`Go to ${it.type} ${i + 1}`}
                >
                  {it.type === 'image' ? (
                    <img src={it.src} alt={`thumb-${i + 1}`} loading="lazy" />
                  ) : (
                    <div className="thumb-video">
                      <video muted preload="metadata" src={`${it.src}#t=0.1`} />
                      <span className="thumb-play">▶</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CatalogTile({ book, openModal }) {
  const scrollRef = useRef();

  const scroll = (dir) => {
    scrollRef.current?.scrollBy({
      left: dir === 'left' ? -300 : 300,
      behavior: 'smooth',
    });
  };

  // Build the items list once per book
  const items = [
    ...book.images.map((img) => ({
      type: 'image',
      // Keep casing consistent — use "Catalog" (capital C) everywhere or change both to "catalog"
      src: `/assets/Catalog/${book.folder}/${img}`,
    })),
    ...(book.video
      ? [{
          type: 'video',
          src: `/assets/Catalog/${book.folder}/${book.video}`, // ↔ changed to "Catalog" to match images
        }]
      : []),
  ];

  return (
    <div className="catalog-tile">
      <h3 className="catalog-title">{book.title}</h3>

      <div
        className="media-container"
        onMouseEnter={() => scrollRef.current?.classList.add('hovered')}
        onMouseLeave={() => scrollRef.current?.classList.remove('hovered')}
      >
        <button className="scroll-btn left" onClick={() => scroll('left')}>❮</button>

        <div className="scrollable-content" ref={scrollRef}>
          {book.images.map((img, i) => {
            const src = `/assets/Catalog/${book.folder}/${img}`;
            return (
              <img
                key={i}
                src={src}
                alt={`${book.title}-${i}`}
                onClick={() => openModal(items, i, book.title)}
                onContextMenu={(e) => e.preventDefault()}
                draggable={false}
              />
            );
          })}

          {book.video && (
            <div
              className="video-thumbnail"
              onClick={() => openModal(items, book.images.length, book.title)}
            >
              <video
                muted
                preload="metadata"
                src={`/assets/Catalog/${book.folder}/${book.video}#t=0.1`}
                onContextMenu={(e) => e.preventDefault()}
              />
              <div className="play-overlay">▶</div>
            </div>
          )}
        </div>

        <button className="scroll-btn right" onClick={() => scroll('right')}>❯</button>
      </div>
    </div>
  );
}
